import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { submitLead } from '../lib/leadSubmit';
import { useSeo } from '../lib/useSeo';
import {
  Users, HeartHandshake, CheckCircle2, ArrowRight, Phone, ChevronDown,
  Briefcase, ShieldCheck, Building2, Droplets, Award,
} from 'lucide-react';

const products = {
  'group-term': {
    name: 'Group Term Insurance',
    tagline: 'Affordable life cover for your entire team',
    description:
      'Group Term Insurance offers pure life protection to all members of a group — employees, association members, or affinity groups — at a fraction of individual premiums. The sum assured is paid to nominees in case of unfortunate demise of a covered member during the policy term.',
    accent: 'teal',
    icon: Users,
    heroGradient: 'from-teal-50 via-white to-emerald-50',
    startingPrice: '75/member/year',
    coverUpTo: '₹1 Crore',
    benefits: [
      { title: 'Lowest Premiums', desc: 'Up to 40% cheaper than individual term plans for the same cover.', icon: Award },
      { title: 'Simple Underwriting', desc: 'No medical tests for most members — fast onboarding for the whole group.', icon: CheckCircle2 },
      { title: 'Flexible Sum Assured', desc: 'Choose uniform cover or tiered cover by designation / salary multiples.', icon: Briefcase },
      { title: 'Optional Riders', desc: 'Add accidental death, disability, and critical illness riders as needed.', icon: ShieldCheck },
      { title: 'Tax Benefits', desc: 'Premiums paid by employer are fully deductible as business expense.', icon: HeartHandshake },
      { title: 'Portable Add-ons', desc: 'Retiring or resigning members can convert to individual cover without fresh medicals.', icon: ArrowRight },
    ],
    coverage: [
      'Death of a member during the policy term',
      'Accidental death benefit (optional rider)',
      'Permanent / partial disability (optional rider)',
      'Critical illness lump-sum (optional rider)',
      'Terminal illness payout',
      'Waiver of premium on disability',
      'Spouse / dependent cover add-on',
      'Conversion to individual policy on exit',
    ],
    faqs: [
      { q: 'Who can buy Group Term Insurance?', a: 'Any formal group — employer, association, trust, bank, cooperative, MFI — with 10+ members can buy a group term cover.' },
      { q: 'Can sum assured differ between members?', a: 'Yes. You can choose uniform cover, salary-multiple-based, or designation-based tiered cover.' },
      { q: 'Are medical tests required?', a: 'Usually no medicals are required up to a Free Cover Limit. Members seeking cover above that may need medical tests.' },
      { q: 'Is GST applicable?', a: 'Yes, 18% GST applies on group term premium. Employer-paid premium is fully tax-deductible.' },
    ],
  },
  'group-health': {
    name: 'Group Health Insurance',
    tagline: 'Comprehensive healthcare cover for your people',
    description:
      'Group Health Insurance (GMC) provides cashless hospitalisation, maternity cover, and day-care benefits to all employees and their dependents under a single employer-sponsored policy. Trusted by 500+ SMEs and corporates across India.',
    accent: 'emerald',
    icon: HeartHandshake,
    heroGradient: 'from-emerald-50 via-white to-teal-50',
    startingPrice: '3,500/member/year',
    coverUpTo: '₹10 Lakhs',
    benefits: [
      { title: 'Cashless Network', desc: '10,000+ network hospitals for cashless admission, zero paperwork.', icon: CheckCircle2 },
      { title: 'Family Floater', desc: 'Cover employee + spouse + up to 2 kids + optional parents in one policy.', icon: Users },
      { title: 'Maternity Benefit', desc: 'Normal & C-section delivery + newborn cover from day 1.', icon: HeartHandshake },
      { title: 'No Waiting Period', desc: 'Waiting period for pre-existing diseases waived for group policies.', icon: ShieldCheck },
      { title: 'Wellness Programmes', desc: 'Annual health check-ups, teleconsultations, mental-wellness sessions.', icon: Award },
      { title: 'Dedicated Helpdesk', desc: 'A relationship manager for claim support & employee queries 24×7.', icon: Phone },
    ],
    coverage: [
      'In-patient hospitalisation (room, ICU, nursing)',
      'Pre & post-hospitalisation expenses',
      'Maternity & newborn baby cover (optional)',
      'Day-care procedures (cataract, chemo, dialysis)',
      'Pre-existing diseases from day 1',
      'Ambulance charges',
      'Parents cover (optional add-on)',
      'Outpatient (OPD) top-up (optional)',
    ],
    faqs: [
      { q: 'What is the minimum group size?', a: 'Most insurers offer group health from a minimum of 7 employees. SME-specific plans start from 5 lives.' },
      { q: 'Can dependants be covered?', a: 'Yes — spouse, up to 2 children, and optionally parents / in-laws can be added as dependants.' },
      { q: 'Are pre-existing diseases covered?', a: 'Yes, typically from day 1 in a group policy (unlike individual policies where a waiting period applies).' },
      { q: 'Is the premium tax-deductible?', a: 'Yes, the premium paid by the employer is fully tax-deductible as a business expense.' },
    ],
  },
  'hdfc-life': {
    name: 'HDFC Life Insurance',
    tagline: 'Build a rewarding career in life and term insurance',
    description:
      'Join Insure RiskFree Solutions as a business associate and start a flexible career path in life and term insurance with training, support, and growth opportunities.',
    accent: 'teal',
    icon: ShieldCheck,
    heroGradient: 'from-teal-50 via-white to-emerald-50',
    startingPrice: 'Flexible opportunity',
    coverUpTo: 'Life & term insurance business',
    benefits: [
      { title: 'Zero Investment', desc: 'Begin your journey without any upfront investment.', icon: Award },
      { title: 'Training & Guidance', desc: 'Receive complete onboarding support and continuous mentoring.', icon: Briefcase },
      { title: 'Flexible Work Style', desc: 'Choose part-time or full-time engagement based on your availability.', icon: Phone },
      { title: 'Career Growth', desc: 'Build your network, strengthen your income, and grow with us.', icon: HeartHandshake },
    ],
    coverage: [
      'Life and term insurance business opportunities',
      'Support for individuals aged 20 to 70',
      'Suitable for freshers, professionals, and experienced agents',
      'Ideal for those seeking flexible earnings with guidance',
      'A trusted platform to start your association journey',
    ],
    faqs: [
      { q: 'Who can join?', a: 'Individuals aged 20 to 70 years with SSLC, PUC, or degree qualification can apply.' },
      { q: 'Who is this best suited for?', a: 'This opportunity is especially relevant for life and term insurance-related associations and professionals.' },
    ],
  },
  'care-health': {
    name: 'Care Health Insurance',
    tagline: 'Start your journey in health insurance with confidence',
    description:
      'Join Insure RiskFree Solutions as a business associate and explore a flexible path in health insurance with training, support, and a strong growth opportunity.',
    accent: 'emerald',
    icon: HeartHandshake,
    heroGradient: 'from-emerald-50 via-white to-teal-50',
    startingPrice: 'Flexible opportunity',
    coverUpTo: 'Health insurance business',
    benefits: [
      { title: 'Zero Investment', desc: 'Start your business journey without any upfront capital.', icon: CheckCircle2 },
      { title: 'Training & Mentorship', desc: 'Get complete guidance and ongoing support from day one.', icon: Users },
      { title: 'Flexible Schedule', desc: 'Work part-time or full-time according to your comfort.', icon: ShieldCheck },
      { title: 'Growth Focused', desc: 'Build long-term income opportunities with a reliable support system.', icon: Award },
    ],
    coverage: [
      'Health insurance business opportunities',
      'Support for individuals aged 20 to 70',
      'Best suited for health insurance-focused professionals and associations',
      'Ideal for those seeking a flexible business path',
      'A trusted platform to begin your journey',
    ],
    faqs: [
      { q: 'Who can join?', a: 'Individuals aged 20 to 70 years with SSLC, PUC, or degree qualification can apply.' },
      { q: 'Who is this best suited for?', a: 'This opportunity is especially relevant for health insurance-related associations and professionals.' },
    ],
  },
  'personal-accident': {
    name: 'Personal Accident Insurance',
    tagline: 'Protect your income, family, and future from accidents',
    description:
      'Personal Accident Insurance provides financial support in case of accidental death, disability, or serious injury. It helps families manage major expenses and preserve stability during difficult times.',
    accent: 'orange',
    icon: HeartHandshake,
    heroGradient: 'from-orange-50 via-white to-amber-50',
    startingPrice: '₹299/year',
    coverUpTo: '₹50 Lakhs',
    benefits: [
      { title: 'Accidental Death Cover', desc: 'Financial protection for your family in case of accidental death.', icon: ShieldCheck },
      { title: 'Disability Protection', desc: 'Receive compensation if an accident causes permanent or partial disability.', icon: Award },
      { title: 'Affordable Premiums', desc: 'Simple plans with low premiums and strong financial support.', icon: CheckCircle2 },
      { title: 'Quick Claim Support', desc: 'Fast assistance for claim filing and policy guidance.', icon: Phone },
    ],
    coverage: [
      'Accidental death benefit',
      'Permanent total disability cover',
      'Permanent partial disability cover',
      'Temporary total disability benefit',
      'Hospital expense support (where applicable)',
      'Family financial protection during emergencies',
    ],
    faqs: [
      { q: 'Who should buy this cover?', a: 'Anyone who wants financial protection for themselves and their family against unforeseen accidents.' },
      { q: 'Is it useful for salaried individuals?', a: 'Yes, it is especially useful for working professionals, business owners, and family breadwinners.' },
    ],
  },
  'home-insurance': {
    name: 'Home Insurance',
    tagline: 'Secure your home, your belongings, and your peace of mind',
    description:
      'Home Insurance protects your building, interiors, valuables, and liability against risks such as fire, burglary, theft, storms, and accidental damage.',
    accent: 'amber',
    icon: Building2,
    heroGradient: 'from-amber-50 via-white to-orange-50',
    startingPrice: '₹1,200/year',
    coverUpTo: '₹2 Crore',
    benefits: [
      { title: 'Structural Protection', desc: 'Cover for walls, roof, flooring, and built-in fixtures.', icon: Building2 },
      { title: 'Contents Cover', desc: 'Protect your furniture, appliances, electronics, and valuables.', icon: ShieldCheck },
      { title: 'Natural Calamity Cover', desc: 'Cover for events such as storms, floods, and earthquakes (where applicable).', icon: Award },
      { title: 'Theft & Burglary Cover', desc: 'Financial support for loss due to burglary or theft.', icon: CheckCircle2 },
    ],
    coverage: [
      'Building structure cover',
      'Home contents cover',
      'Theft and burglary protection',
      'Fire and natural disaster cover',
      'Liability protection for third-party injury or property damage',
      'Optional add-ons for valuables and home appliances',
    ],
    faqs: [
      { q: 'Does this cover the entire house?', a: 'Yes, depending on the chosen plan, it can cover the building and your belongings inside it.' },
      { q: 'Can I include valuables?', a: 'Yes, most plans allow you to add optional cover for jewellery, electronics, and other valuables.' },
    ],
  },
  'workmen-compensation': {
    name: 'Workmen Compensation Insurance',
    tagline: 'Support your workforce and meet employer responsibilities',
    description:
      'Workmen Compensation Insurance helps employers manage legal and financial liabilities arising from work-related injuries, accidents, or death of employees.',
    accent: 'teal',
    icon: Briefcase,
    heroGradient: 'from-teal-50 via-white to-emerald-50',
    startingPrice: 'Custom quote',
    coverUpTo: 'As per statutory limits',
    benefits: [
      { title: 'Statutory Compliance', desc: 'Helps meet legal obligations for employee injury and accident cover.', icon: ShieldCheck },
      { title: 'Financial Liability Protection', desc: 'Protects your business from compensation claims and legal expenses.', icon: Briefcase },
      { title: 'Employee Welfare', desc: 'Shows care for your team during unforeseen workplace incidents.', icon: HeartHandshake },
      { title: 'Flexible Coverage', desc: 'Tailored to suit factories, offices, contractors, and service businesses.', icon: Users },
    ],
    coverage: [
      'Workplace injury compensation',
      'Accident and disability benefit',
      'Death benefit for employees',
      'Legal liability protection for employers',
      'Coverage for temporary and permanent injury cases',
    ],
    faqs: [
      { q: 'Is this mandatory for businesses?', a: 'In many cases, employers are required to provide this protection as part of statutory compliance.' },
      { q: 'Who can buy this policy?', a: 'This is typically suitable for factories, contractors, service providers, and other organisations with employees.' },
    ],
  },
  'fire-burglary': {
    name: 'Fire and Burglary Insurance',
    tagline: 'Protect your property from fire, theft, and sudden losses',
    description:
      'Fire and Burglary Insurance offers financial protection against losses from fire, explosion, lightning, theft, and burglary at your premises.',
    accent: 'emerald',
    icon: ShieldCheck,
    heroGradient: 'from-emerald-50 via-white to-teal-50',
    startingPrice: '₹1,500/year',
    coverUpTo: '₹3 Crore',
    benefits: [
      { title: 'Fire Damage Cover', desc: 'Financial support for damage caused by fire, lightning, or explosion.', icon: ShieldCheck },
      { title: 'Burglary Protection', desc: 'Support for theft and burglary-related losses at your premises.', icon: CheckCircle2 },
      { title: 'Business Continuity', desc: 'Helps reduce disruption and recover faster after an incident.', icon: Briefcase },
      { title: 'Customisable Plans', desc: 'Choose coverage based on your property type, value, and risks.', icon: Award },
    ],
    coverage: [
      'Fire and explosion damage',
      'Theft and burglary loss',
      'Damage to stock and property',
      'Business interruption support (where applicable)',
      'Flexible cover for homes and commercial premises',
    ],
    faqs: [
      { q: 'Can this cover both business and home properties?', a: 'Yes, the policy can be tailored to suit homes, shops, offices, and other premises.' },
      { q: 'Is stock covered?', a: 'Depending on the plan, stock and business contents can be included in the cover.' },
    ],
  },
  'marine-insurance': {
    name: 'Marine Insurance',
    tagline: 'Safeguard your cargo and shipments during transit',
    description:
      'Marine Insurance provides protection for goods and cargo during domestic and international transit against damage, theft, water ingress, and unexpected accidents.',
    accent: 'teal',
    icon: Droplets,
    heroGradient: 'from-teal-50 via-white to-slate-50',
    startingPrice: 'Custom quote',
    coverUpTo: 'As per shipment value',
    benefits: [
      { title: 'Cargo Protection', desc: 'Covers goods while they are being transported by sea, air, or land.', icon: Droplets },
      { title: 'Transit Risk Cover', desc: 'Supports losses caused by accidents, theft, or handling damage.', icon: ShieldCheck },
      { title: 'Flexible Terms', desc: 'Policies can be tailored to your shipping route and cargo type.', icon: Briefcase },
      { title: 'Global Reach', desc: 'Ideal for importers, exporters, and businesses with regular logistics needs.', icon: Award },
    ],
    coverage: [
      'Cargo and shipment cover',
      'Loss or damage during transit',
      'Theft and pilferage protection',
      'Water ingress and accidental damage',
      'Custom cover for domestic and international shipments',
    ],
    faqs: [
      { q: 'Who should buy this policy?', a: 'Importers, exporters, traders, and businesses with regular goods transportation can benefit from it.' },
      { q: 'Can it be used for small shipments?', a: 'Yes, coverage can be arranged for both small and large consignments depending on your needs.' },
    ],
  },
  'sattva-organic-mangoes': {
    name: 'Sattva Products - Organic Mangoes',
    tagline: 'Fresh, organic mangoes sourced with care',
    description:
      'Bring home naturally ripened organic mangoes that are hand-picked, carefully packed, and delivered fresh for a premium seasonal experience.',
    accent: 'amber',
    icon: Award,
    heroGradient: 'from-amber-50 via-white to-orange-50',
    startingPrice: '₹150/kg',
    coverUpTo: 'Pan-India delivery',
    benefits: [
      { title: 'Freshly Hand-Picked', desc: 'Selected at peak ripeness for rich aroma and natural sweetness.', icon: CheckCircle2 },
      { title: 'Organic Quality', desc: 'Carefully sourced and packed to preserve freshness and purity.', icon: ShieldCheck },
      { title: 'Seasonal Availability', desc: 'Delivered during the mango season with a premium quality promise.', icon: HeartHandshake },
      { title: 'Safe Packaging', desc: 'Packed securely to protect the fruit during transit.', icon: Droplets },
    ],
    coverage: [
      'Seasonal organic mango delivery',
      'Hand-picked premium produce',
      'Secure packaging for safe transit',
      'Freshness-focused delivery experience',
      'Pan-India dispatch support',
    ],
    faqs: [
      { q: 'When are the mangoes available?', a: 'They are available during the mango season and dispatch is arranged as soon as fresh stock is ready.' },
      { q: 'Do you deliver nationwide?', a: 'Yes, we arrange Pan-India delivery for the seasonal product.' },
    ],
  },
};

const accentClasses = {
  emerald: { text: 'text-emerald-600', bg: 'bg-emerald-600', bgLight: 'bg-emerald-50', hover: 'hover:bg-emerald-700', chip: 'bg-emerald-100 text-emerald-700' },
  teal: { text: 'text-teal-600', bg: 'bg-teal-600', bgLight: 'bg-teal-50', hover: 'hover:bg-teal-700', chip: 'bg-teal-100 text-teal-700' },
  orange: { text: 'text-orange-600', bg: 'bg-orange-600', bgLight: 'bg-orange-50', hover: 'hover:bg-orange-700', chip: 'bg-orange-100 text-orange-700' },
  amber: { text: 'text-amber-600', bg: 'bg-amber-600', bgLight: 'bg-amber-50', hover: 'hover:bg-amber-700', chip: 'bg-amber-100 text-amber-700' },
};

const OtherProductPage = () => {
  const { slug } = useParams();
  const product = products[slug];
  const isBusinessAssociate = slug === 'hdfc-life' || slug === 'care-health';
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: '', phone: '', org: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useSeo({
    title: product ? `${product.name} | InsureRiskFree Solutions` : 'Our Products | InsureRiskFree Solutions',
    description: product ? product.description : 'Discover our insurance and product offerings with expert support from InsureRiskFree Solutions.',
    canonical: product ? `https://insureriskfree.com/products/${slug}` : 'https://insureriskfree.com/',
    url: product ? `https://insureriskfree.com/products/${slug}` : 'https://insureriskfree.com/',
    ogImage: 'https://insureriskfree.com/og-image.jpg',
    twitterImage: 'https://insureriskfree.com/og-image.jpg',
    keywords: product ? `${product.name.toLowerCase()}, ${product.name.toLowerCase()} enquiry, ${product.name.toLowerCase()} insurance solutions` : 'insurance product, expert insurance solutions, product details',
  });

  if (!product) return <Navigate to="/" replace />;

  const a = accentClasses[product.accent];
  const Icon = product.icon;
  const enquiryLabel = isBusinessAssociate ? 'Enquiry' : slug === 'sattva-organic-mangoes' ? 'Order' : 'Quote';
  const orgLabel = isBusinessAssociate ? 'Address / Pincode (optional)' : 'Company name (optional)';
  const primaryCtaLabel = isBusinessAssociate ? 'Request Business Associate Enquiry' : slug === 'sattva-organic-mangoes' ? 'Order Now' : `Get ${enquiryLabel}`;
  const breadcrumbLabel = isBusinessAssociate ? 'Business Associate' : slug === 'sattva-organic-mangoes' ? 'Our Other Products' : 'Insurance';
  const businessAssociateInfo = isBusinessAssociate ? {
    title: 'Join Insure RiskFree Solutions as a Business Associate',
    intro: slug === 'hdfc-life'
      ? 'Build your future in life and term insurance with zero investment, training support, and unlimited earning potential.'
      : 'Build your future in health insurance with zero investment, training support, and unlimited earning potential.',
    focus: slug === 'hdfc-life'
      ? 'Best suited for Life & Term Insurance-related associations.'
      : 'Best suited for Health Insurance business opportunities.',
    bullets: [
      'Zero Investment to start',
      'Complete Training & Ongoing Support',
      'Flexible work as part-time or full-time',
      'Build your income with dedication and consistent effort',
    ],
    eligibility: 'Age 20 to 70 years • Education: SSLC / PUC / Degree',
    documents: ['Aadhaar Card', 'PAN Card', 'Marks Card / Educational Certificate', '2 Passport-size Photographs'],
    alternateLink: slug === 'hdfc-life'
      ? { label: 'Looking for Health Insurance business?', to: '/products/care-health' }
      : { label: 'Looking for Life & Term Insurance business?', to: '/products/hdfc-life' },
  } : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || form.phone.length !== 10) {
      setFormError('Please enter your name and a valid 10-digit phone number.');
      return;
    }
    setFormError('');
    setSubmitting(true);
    const result = await submitLead({
      type: product.name,
      name: form.name,
      phone: form.phone,
      city: form.org,
    });
    setSubmitting(false);
    if (!result.ok) {
      setFormError('Unable to submit right now. Please try again in a moment or contact us by phone.');
      return;
    }
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setForm({ name: '', phone: '', org: '' }); }, 5000);
  };

  return (
    <div className="bg-white">
      <div className="border-b border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs sm:text-sm">
          <nav className="flex flex-wrap items-center gap-2 text-slate-500">
            <Link to="/" className="hover:text-[#00B48E]">Home</Link>
            <span>/</span>
            <span className="text-slate-700">{breadcrumbLabel}</span>
            <span>/</span>
            <span className={`font-medium ${a.text} break-words`}>{product.name}</span>
          </nav>
        </div>
      </div>

      <section className={`relative overflow-hidden bg-gradient-to-br ${product.heroGradient}`}>
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className={`absolute top-10 -left-16 w-72 h-72 rounded-full ${a.bgLight} blur-3xl`} />
          <div className={`absolute bottom-10 -right-20 w-96 h-96 rounded-full ${a.bgLight} blur-3xl`} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`inline-flex items-center gap-2 ${a.chip} px-4 py-1.5 rounded-full text-sm font-medium mb-5`}>
                <Icon className="w-4 h-4" /> {product.name}
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-[#0F2A4B] leading-[1.1] tracking-tight break-words">{product.tagline}</h1>
              <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-lg leading-relaxed break-words">{product.description}</p>

              <div className="mt-7 flex flex-wrap gap-5">
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Starting from</div>
                  <div className="text-2xl font-bold text-[#0F2A4B]">{product.startingPrice}</div>
                </div>
                <div className="h-12 w-px bg-slate-200" />
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Cover up to</div>
                  <div className="text-2xl font-bold text-[#0F2A4B]">{product.coverUpTo}</div>
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a href="#product-enquiry-form" className={`inline-flex w-full sm:w-auto max-w-full items-center justify-center gap-2 ${a.bg} ${a.hover} text-white rounded-full px-5 sm:px-7 py-4 sm:py-6 text-xs sm:text-base font-semibold shadow-md transition-colors break-words whitespace-normal leading-tight`}>
                  {primaryCtaLabel} <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
                <a href="tel:+917892125055" className="w-full sm:w-auto max-w-full">
                  <Button variant="outline" className="w-full sm:w-auto max-w-full justify-center rounded-full px-5 sm:px-7 py-4 sm:py-6 text-xs sm:text-base font-semibold border-slate-300 text-slate-700 hover:bg-slate-50 whitespace-normal leading-tight">
                    <Phone className="w-4 h-4 mr-2 shrink-0" /> {isBusinessAssociate ? 'Call Now' : 'Talk to Expert'}
                  </Button>
                </a>
              </div>

              {businessAssociateInfo && (
                <div className="mt-8 rounded-3xl border border-emerald-200 bg-white/80 p-6 shadow-sm">
                  <div className="text-sm uppercase tracking-[0.3em] text-emerald-700 font-semibold break-words">Business Associate Opportunity</div>
                  <h2 className="mt-2 text-xl sm:text-2xl font-bold text-[#0F2A4B] break-words">{businessAssociateInfo.title}</h2>
                  <p className="mt-3 text-sm sm:text-base text-slate-600 break-words">{businessAssociateInfo.intro}</p>
                  <p className="mt-3 font-semibold text-emerald-700 break-words">{businessAssociateInfo.focus}</p>
                  <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                    {businessAssociateInfo.bullets.map((bullet) => (
                      <li key={bullet} className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-slate-700">{bullet}</li>
                    ))}
                  </ul>
                  <div className="mt-5 text-sm text-slate-600">
                    <div className="font-semibold text-[#0F2A4B]">Eligibility</div>
                    <div className="mt-1">{businessAssociateInfo.eligibility}</div>
                  </div>
                  <div className="mt-4 text-sm text-slate-600">
                    <div className="font-semibold text-[#0F2A4B]">Required Documents</div>
                    <ul className="mt-2 space-y-1">
                      {businessAssociateInfo.documents.map((doc) => (
                        <li key={doc} className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" />{doc}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-3 text-sm">
                    <a href="tel:+916364849979" className="font-semibold text-[#00B48E] hover:text-[#0F2A4B]">Call: 6364849979</a>
                    <a href="tel:+917892125055" className="font-semibold text-[#00B48E] hover:text-[#0F2A4B]">Call: 7892125055</a>
                    <Link to={businessAssociateInfo.alternateLink.to} className="font-semibold text-[#0F2A4B] hover:text-[#00B48E]">{businessAssociateInfo.alternateLink.label}</Link>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8" id="product-enquiry-form">
              <div className="flex items-start gap-3 mb-5">
                <div className={`w-11 h-11 rounded-xl ${a.bgLight} ${a.text} flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-[#0F2A4B] break-words text-sm sm:text-base leading-tight">{isBusinessAssociate ? `Request ${product.name} Business Associate Enquiry` : `Request ${product.name} ${enquiryLabel}`}</div>
                  <div className="text-xs text-slate-500">Our team will reach you within 5 minutes</div>
                </div>
              </div>

              {submitted ? (
                <div className="py-10 text-center">
                  <div className={`w-16 h-16 rounded-full ${a.bgLight} flex items-center justify-center mx-auto mb-4`}>
                    <CheckCircle2 className={`w-9 h-9 ${a.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F2A4B]">Thanks, {form.name.split(' ')[0] || 'friend'}!</h3>
                  <p className="text-slate-600 text-sm mt-1">Our team will reach out on +91 {form.phone} shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  {formError && <p className="text-sm text-rose-600">{formError}</p>}
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#00B48E]"
                    required
                  />
                  <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden focus-within:border-[#00B48E]">
                    <span className="px-3 py-3 bg-slate-50 text-slate-700 text-sm font-medium border-r">+91</span>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder="10-digit mobile number"
                      className="flex-1 px-3 py-3 text-sm outline-none"
                      required
                    />
                  </div>
                  <input
                    value={form.org}
                    onChange={(e) => setForm({ ...form, org: e.target.value })}
                    placeholder={orgLabel}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#00B48E]"
                  />
                  <Button type="submit" disabled={submitting} className={`w-full ${a.bg} ${a.hover} text-white rounded-xl py-4 sm:py-6 text-xs sm:text-base font-semibold disabled:opacity-60 whitespace-normal leading-tight`}>
                    {submitting ? 'Submitting...' : isBusinessAssociate ? `Request ${product.name} Business Associate Enquiry` : `Request ${enquiryLabel}`}
                  </Button>
                  <p className="text-[11px] text-slate-500 text-center pt-1">By submitting, you agree to be contacted by our team.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2A4B]">Why choose {product.name}?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {product.benefits.map((b) => {
              const BIcon = b.icon;
              return (
                <div key={b.title} className="group bg-white rounded-2xl border border-slate-100 p-6 hover:-translate-y-1 hover:shadow-lg hover:border-emerald-200 transition-all">
                  <div className={`w-12 h-12 rounded-xl ${a.bgLight} ${a.text} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <BIcon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F2A4B] mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {isBusinessAssociate ? (
        <section className="py-16 lg:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2A4B]">Why this opportunity matters</h2>
              <p className="mt-3 text-slate-600">This role is designed for individuals who want a flexible, low-investment way to build a career in insurance with guidance and support.</p>
              <div className="mt-7 rounded-2xl bg-white border border-slate-100 p-5">
                <div className="font-semibold text-[#0F2A4B]">Who can benefit</div>
                <div className="mt-2 text-sm text-slate-600">Freshers, working professionals, retired individuals, and experienced agents can all start with the right support and dedication.</div>
              </div>
            </div>
            <ul className="bg-white rounded-2xl border border-slate-100 p-3 divide-y divide-slate-100">
              {product.coverage.map((c) => (
                <li key={c} className="flex items-start gap-3 p-4">
                  <CheckCircle2 className={`w-5 h-5 ${a.text} shrink-0 mt-0.5`} />
                  <span className="text-sm text-slate-700">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2A4B]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {product.faqs.map((f, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between text-left p-5 hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-[#0F2A4B] pr-4">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl sm:text-2xl font-bold text-[#0F2A4B] mb-6">Explore our other products</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(products).filter(([k]) => k !== slug).map(([key, p]) => {
              const PIcon = p.icon;
              const pa = accentClasses[p.accent];
              return (
                <Link key={key} to={`/products/${key}`} className="group bg-white rounded-2xl border border-slate-100 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
                  <div className={`w-11 h-11 rounded-xl ${pa.bgLight} ${pa.text} flex items-center justify-center mb-3`}>
                    <PIcon className="w-5 h-5" />
                  </div>
                  <div className="font-semibold text-[#0F2A4B]">{p.name}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#00B48E] group-hover:gap-2 transition-all">
                    Explore <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default OtherProductPage;
